import { parseEmbeddedSignupMessage, type MetaWhatsappSession } from "./meta-whatsapp-events";

export type { MetaWhatsappSession } from "./meta-whatsapp-events";

interface FacebookLoginResponse {
  authResponse?: { code?: string };
}

interface FacebookSdk {
  init(options: { appId: string; autoLogAppEvents: boolean; xfbml: boolean; version: string }): void;
  login(callback: (response: FacebookLoginResponse) => void, options: Record<string, unknown>): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

let sdkPromise: Promise<FacebookSdk> | null = null;

function loadSdk(appId: string): Promise<FacebookSdk> {
  if (window.FB) {
    window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version: "v24.0" });
    return Promise.resolve(window.FB);
  }
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new Error("Meta non ha caricato la finestra di collegamento")), 15_000);
    window.fbAsyncInit = () => {
      window.clearTimeout(timeout);
      if (!window.FB) return reject(new Error("SDK Meta non disponibile"));
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version: "v24.0" });
      resolve(window.FB);
    };
    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.src = "https://connect.facebook.net/it_IT/sdk.js";
    script.onerror = () => {
      window.clearTimeout(timeout);
      sdkPromise = null;
      reject(new Error("Impossibile caricare il collegamento sicuro Meta"));
    };
    document.head.appendChild(script);
  });
  return sdkPromise;
}

function trustedFacebookOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    return url.protocol === "https:" && (url.hostname === "facebook.com" || url.hostname.endsWith(".facebook.com"));
  } catch {
    return false;
  }
}

export async function launchMetaWhatsappSignup(appId: string, configId: string): Promise<{ code: string; session: MetaWhatsappSession }> {
  const facebook = await loadSdk(appId);
  return new Promise((resolve, reject) => {
    let code = "";
    let session: MetaWhatsappSession | null = null;
    let settled = false;
    let timeout = 0;

    const cleanup = () => {
      window.clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
    };
    const finish = (error?: Error) => {
      if (settled) return;
      if (!error && code && session) {
        settled = true;
        cleanup();
        resolve({ code, session });
      } else if (error) {
        settled = true;
        cleanup();
        reject(error);
      }
    };
    const onMessage = (event: MessageEvent) => {
      if (!trustedFacebookOrigin(event.origin)) return;
      const parsed = parseEmbeddedSignupMessage(event.data);
      if (!parsed) return; // I frame Meta inviano anche messaggi non relativi all'iscrizione.
      if (parsed.kind === "cancel") return finish(new Error("Collegamento Meta interrotto prima del completamento"));
      if (parsed.kind === "error") return finish(new Error(parsed.message));
      session = parsed.session;
      finish();
    };

    timeout = window.setTimeout(() => finish(new Error("Collegamento Meta scaduto, riprova")), 10 * 60 * 1000);
    window.addEventListener("message", onMessage);
    facebook.login((response) => {
      code = response.authResponse?.code || "";
      if (!code) return finish(new Error("Collegamento Meta annullato"));
      if (!session) {
        // Il codice è arrivato ma i dati dell'account no: non aspettare 10 minuti, il codice scade presto.
        window.clearTimeout(timeout);
        timeout = window.setTimeout(() => finish(new Error("Meta non ha inviato i dati dell'account collegato. Controlla in WhatsApp Manager prima di riprovare.")), 60_000);
      }
      finish();
    }, {
      config_id: configId,
      response_type: "code",
      override_default_response_type: true,
      extras: {
        sessionInfoVersion: "3",
        version: "v4",
        featureType: "whatsapp_business_app_onboarding",
      },
    });
  });
}
